import { ClientItemsResponse } from 'app/common/types'

import { supportedConnectionConfigs } from '../application/supportedConnectionConfigs'
import { AdtributeConnection } from './adtribute'
import {
  AmazonConnection,
  AmazonConnectionConfig,
  AmazonConnectionEntity,
  InstalledAmazonConnection,
} from './amazon'
import {
  CriteoConnection,
  CriteoConnectionConfig,
  CriteoConnectionEntity,
  InstalledCriteoConnection,
} from './criteo'
import {
  DV360Connection,
  DV360ConnectionConfig,
  DV360ConnectionEntity,
  InstalledDV360Connection,
} from './dv360'
import {
  FacebookConnection,
  FacebookConnectionConfig,
  FacebookConnectionEntity,
  InstalledFacebookConnection,
} from './facebook'
import {
  GoogleAdwordsConnection,
  GoogleAdwordsConnectionConfig,
  GoogleAdwordsConnectionEntity,
  InstalledGoogleAdwordsConnection,
} from './googleAdwords'
import {
  GoogleAnalyticsConnection,
  GoogleAnalyticsConnectionConfig,
  GoogleAnalyticsConnectionEntity,
  InstalledGoogleAnalyticsConnection,
} from './googleAnalytics'
import {
  GoogleBigqueryConnection,
  GoogleBigqueryConnectionConfig,
  GoogleBigqueryConnectionEntity,
  InstalledGoogleBigqueryConnection,
} from './googleBigquery'
import {
  InnkeeprConnection,
  InnkeeprConnectionConfig,
  InnkeeprConnectionEntity,
  InstalledInnkeeprConnection,
} from './innkeepr'
import { OAuthUser } from './oauthUser'
import {
  InstalledShopifyConnection,
  ShopifyConfig,
  ShopifyConnection,
  ShopifyEntity,
} from './shopify'
import {
  InstalledTiktokConnection,
  TiktokConnection,
  TiktokConnectionConfig,
  TiktokConnectionEntity,
} from './tiktok'
import {
  InstalledWoocommerceConnection,
  WoocommerceConnection,
  WoocommerceConnectionConfig,
  WoocommerceConnectionEntity,
} from './woocommerce'

export type ConnectionCapability = 'advertising' | 'attribution' | 'events' | 'profiles' | 'signals'

export type ConnectionCategory =
  | 'advertising'
  | 'analytics'
  | 'warehouse'
  | 'website'
  | 'crm'
  | 'shopsystem'

export type ConnectionName =
  | 'amazon'
  | 'adtribute'
  | 'criteo'
  | 'dv360'
  | 'facebook'
  | 'googleAdwords'
  | 'googleAnalytics'
  | 'googleBigquery'
  | 'innkeepr'
  | 'shopify'
  | 'tiktok'
  | 'woocommerce'

export type ConnectionStatus = 'active' | 'disabled' | 'draft'

export interface Connection<N extends ConnectionName> {
  capabilities: ConnectionCapability[]
  category: ConnectionCategory
  name: N
}

interface ConnectionCreator {
  firstName: string
  lastName: string
}

export interface InstalledConnectionConfig {
  authorizationUser: OAuthUser | null
  createdAt: string | null
  createdBy: ConnectionCreator | null
  id: string
  options: {
    targetingAvailable: boolean
    urlCampaignParam?: string
    urlTrackingParam: string | null
  }
  status: ConnectionStatus
  error?: string
  targetingAvailable: boolean
}

export interface ConnectionEntityType<N extends ConnectionName, C>
  extends Connection<N>,
    InstalledConnectionConfig {
  config: C
  deprecationError?: string
}

export interface AvailableConnectionsEntity {
  capabilities: ConnectionCapability[]
  category: ConnectionCategory
  name: string
}

export type ConnectionEntity =
  | AmazonConnectionEntity
  | CriteoConnectionEntity
  | DV360ConnectionEntity
  | FacebookConnectionEntity
  | GoogleAdwordsConnectionEntity
  | GoogleAnalyticsConnectionEntity
  | GoogleBigqueryConnectionEntity
  | InnkeeprConnectionEntity
  | ShopifyEntity
  | TiktokConnectionEntity
  | WoocommerceConnectionEntity

export type AvailableConnectionsGetResponse = ClientItemsResponse<AvailableConnectionsEntity>
export type ConnectionsGetResponse = ClientItemsResponse<ConnectionEntity>

export type ValidateRequest<V> = V
export type ValidateResponse = { valid: boolean }

export type ConnectionConfigType<V = string> = Record<string, V>
export type NoConfig = ConnectionConfigType
export type ConnectionConfig =
  | AmazonConnectionConfig
  | CriteoConnectionConfig
  | DV360ConnectionConfig
  | FacebookConnectionConfig
  | GoogleAdwordsConnectionConfig
  | GoogleAnalyticsConnectionConfig
  | GoogleBigqueryConnectionConfig
  | InnkeeprConnectionConfig
  | NoConfig
  | ShopifyConfig
  | TiktokConnectionConfig
  | WoocommerceConnectionConfig

export interface ConnectionAuthConfig {
  params: Record<string, string>
  url: string
}
export type AvailableConnectionConfig<
  C extends ConnectionConfig,
  HasAuthConfig extends boolean = false,
> = {
  config: C
  multiStepSetup: boolean
  readonly: boolean
  readonlyConfig: string[]
} & (HasAuthConfig extends true ? { authConfig: ConnectionAuthConfig } : { authConfig?: never })

export type AvailableConnection =
  | AdtributeConnection
  | AmazonConnection
  | CriteoConnection
  | DV360Connection
  | FacebookConnection
  | GoogleAdwordsConnection
  | GoogleAnalyticsConnection
  | GoogleBigqueryConnection
  | InnkeeprConnection
  | ShopifyConnection
  | TiktokConnection
  | WoocommerceConnection

export type AvailableConnectionWithAuthConfig = Extract<
  AvailableConnection,
  { authConfig: ConnectionAuthConfig }
>

export type InstalledConnection =
  | InstalledAmazonConnection
  | InstalledCriteoConnection
  | InstalledDV360Connection
  | InstalledFacebookConnection
  | InstalledGoogleAdwordsConnection
  | InstalledGoogleAnalyticsConnection
  | InstalledGoogleBigqueryConnection
  | InstalledInnkeeprConnection
  | InstalledShopifyConnection
  | InstalledTiktokConnection
  | InstalledWoocommerceConnection

export type AvailableOrInstalledConnection = AvailableConnection | InstalledConnection

export const isInstalledConnection = (connection: unknown): connection is InstalledConnection =>
  (connection as InstalledConnection).id !== undefined

const mapConnectionConfig = (connEntity: ConnectionEntity): ConnectionConfig => {
  switch (connEntity.name) {
    case 'amazon': {
      return {
        amcInstanceId: connEntity.config.amcInstanceId ?? '',
      } satisfies AmazonConnectionConfig
    }
    case 'criteo': {
      return {
        advertisers: connEntity.config.advertisers || [],
        adSetId: connEntity.options.urlTrackingParam ?? '',
        gumCallerId: connEntity.config.gumCallerId,
      } satisfies CriteoConnectionConfig
    }
    case 'dv360': {
      return {
        advertisers: connEntity.config.advertisers,
        adGroupId: connEntity.options.urlTrackingParam ?? '',
      } satisfies DV360ConnectionConfig
    }
    case 'facebook': {
      return {
        accounts: connEntity.config.adAccounts || [],
        // Prefer the new single field; fall back to the legacy array form.
        pixelId: connEntity.config.pixelId ?? connEntity.config.pixelIds?.[0] ?? '',
        adId: connEntity.options.urlTrackingParam ?? '',
      } satisfies FacebookConnectionConfig
    }
    case 'googleAdwords': {
      return {
        accounts: connEntity.config.adAccounts,
        campaignId: connEntity.options.urlCampaignParam ?? '',
      } satisfies GoogleAdwordsConnectionConfig
    }
    case 'googleAnalytics': {
      return {
        propertyId: connEntity.config.trackingCredentials.id,
        measurementId: connEntity.config.trackingCredentials.measurementId,
        // secret is currently not returned in api
        apiSecret: connEntity.config.trackingCredentials.secret ?? '',
      } satisfies GoogleAnalyticsConnectionConfig
    }
    case 'googleBigquery': {
      return {
        project: connEntity.config.project,
      } satisfies GoogleBigqueryConnectionConfig
    }
    case 'innkeepr': {
      return {
        trackingUrl: connEntity.config.trackingUrl ?? '',
      } satisfies InnkeeprConnectionConfig
    }
    case 'tiktok': {
      return {
        adAccountIds: connEntity.config.advertisers.map((advertiser) => advertiser.id).join(';'),
        adId: connEntity.options.urlTrackingParam ?? 'ttm',
        advertisers: connEntity.config.advertisers,
      } satisfies TiktokConnectionConfig
    }
    case 'shopify': {
      return {
        shops: connEntity.config.map((shop) => ({
          name: shop.shop,
          pixelId: shop.pixel_id || null,
        })),
      } satisfies ShopifyConfig
    }
    case 'woocommerce': {
      return {
        shopbaseUrl: connEntity.config.shopBaseUrl,
        apiKey: connEntity.config.consumerKey ?? '',
        apiSecret: connEntity.config.secret ?? '',
      } satisfies WoocommerceConnectionConfig
    }
  }
}

export const mapToAvailableConnection = (
  entity: AvailableConnectionsEntity,
): AvailableConnection | null => {
  const config = supportedConnectionConfigs.find((scc) => scc.name === entity.name)

  if (!config) return null

  return {
    ...entity,
    ...config,
  } as AvailableConnection
}

export const mapToAvailableConnections = (
  entities: AvailableConnectionsEntity[],
): AvailableConnection[] =>
  entities
    .map((availableConnEntity) => mapToAvailableConnection(availableConnEntity))
    .filter((availableConnection) => availableConnection !== null)

export const mapToConnection = (
  entity: ConnectionEntity,
  availableConnection: AvailableConnection,
): InstalledConnection =>
  ({
    ...availableConnection,
    ...entity,
    authorizationUser: 'user' in entity.config ? entity.config.user : null,
    targetingAvailable: entity.options.targetingAvailable,
    config: mapConnectionConfig(entity),
  }) as InstalledConnection

export const mapToConnections = (
  entities: ConnectionEntity[],
  availableConnections: AvailableConnection[],
): InstalledConnection[] =>
  entities
    .filter((connEntity) => !connEntity.error?.includes('This connection is deprecated'))
    .map((connEntity) =>
      mapToConnection(connEntity, availableConnections.find((ac) => ac.name === connEntity.name)!),
    )
